import { FC } from 'react';

import { CampaignProductType } from 'utils/constants/campaign';

import { Alert, Button, ButtonStack } from '@hh.ru/magritte-ui';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { getUrlToCopyCampaign } from 'components/CampaignsDashboard/utils/utils';

import { CampaignArchiveReason, DashboardCampaign } from 'store/models/campaignsDashboard';

const TrlKeys = {
    archiveReason: 'CampaignsDashboard.CampaignsTable.CampaignStatus.archiveReason',
    copyCampaign: 'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.copyCampaign',
    close: 'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.close',
    reasons: {
        [CampaignArchiveReason.ArchivedByUser]: undefined,
        [CampaignArchiveReason.ArchivedVacancy]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.archivedVacancy',
        [CampaignArchiveReason.ExpiredAccount]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.expiredAccount',
        [CampaignArchiveReason.ObsoleteCampaign]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.obsoleteCampaign',
        [CampaignArchiveReason.OldBlockedCampaign]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.oldBlockedCampaign',
        [CampaignArchiveReason.OldPrices]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.oldPrices',
        [CampaignArchiveReason.OldSource]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.oldSource',
        [CampaignArchiveReason.Transfer]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.transfer',
        [CampaignArchiveReason.WrongAccount]:
            'CampaignsDashboard.CampaignsTable.CampaignStatus.CampaignStatusArchived.wrongAccount',
    },
};

export const CampaignStatusArchivedButton: FC<{ campaign: DashboardCampaign }> = ({ campaign }) => {
    const [isAlertVisible, setIsAlertVisibleOn, setIsAlertVisibleOff] = useOnOffState(false);

    const { id: campaignId, archiveReason, productType, banners } = campaign;
    const trls = useTrls();

    const archiveReasonValue = trls(TrlKeys.reasons[archiveReason]);
    const isAuction = productType === CampaignProductType.SearchAuction;

    if (!archiveReasonValue) {
        return null;
    }

    return (
        <>
            <Button
                mode="secondary"
                style="neutral"
                size="small"
                data-qa={`campaign-status-archived-reason-button-${campaignId}`}
                onClick={setIsAlertVisibleOn}
            >
                {trls(TrlKeys.archiveReason)}
            </Button>
            {archiveReasonValue && (
                <Alert
                    visible={isAlertVisible}
                    title={trls(TrlKeys.archiveReason)}
                    description={archiveReasonValue}
                    buttons={
                        <ButtonStack stretched layout="vertical">
                            {!isAuction &&
                                [
                                    CampaignArchiveReason.ArchivedVacancy,
                                    CampaignArchiveReason.OldPrices,
                                    CampaignArchiveReason.ObsoleteCampaign,
                                    CampaignArchiveReason.OldSource,
                                    CampaignArchiveReason.OldBlockedCampaign,
                                    CampaignArchiveReason.WrongAccount,
                                ].includes(archiveReason) &&
                                banners.length > 0 && (
                                    <Button
                                        mode="secondary"
                                        style="accent"
                                        Element={SPALink}
                                        to={getUrlToCopyCampaign(campaignId, banners)}
                                        data-qa={`campaign-status-archived-alert-copy-button-${campaignId}`}
                                    >
                                        {trls(TrlKeys.copyCampaign)}
                                    </Button>
                                )}
                            <Button
                                mode="secondary"
                                style="neutral"
                                onClick={setIsAlertVisibleOff}
                                data-qa={`campaign-status-archived-alert-close-button-${campaignId}`}
                            >
                                {trls(TrlKeys.close)}
                            </Button>
                        </ButtonStack>
                    }
                    onClose={setIsAlertVisibleOff}
                />
            )}
        </>
    );
};
